/**
 * Main dashboard component with role-based content
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';
import { AdminService } from '../services/admin';
import { labService } from '../services/lab';
import { webSocketService } from '../services/websocket';

export function Dashboard() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [wsConnected, setWsConnected] = useState(false);

  // System health and labs data
  const { data: systemHealth } = useQuery({
    queryKey: ['system-health'],
    queryFn: AdminService.getSystemHealth,
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: 1, // Don't retry too aggressively for health checks
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-count'],
    queryFn: () => labService.getLabs(),
    select: (data) => data || [],
  });

  // Admin-specific data
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: AdminService.getDashboardStats,
    enabled: hasRole(UserRole.ADMIN),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: recentActivity } = useQuery({
    queryKey: ['recent-activity'],
    queryFn: () => AdminService.getRecentActivity(10), // Get last 10 activities
    enabled: hasRole(UserRole.ADMIN),
    refetchInterval: 30000,
  });

  // User-specific data for learners
  const { data: userRuns } = useQuery({
    queryKey: ['user-runs'],
    queryFn: () => labService.getRuns(),
    enabled: hasRole(UserRole.LEARNER),
  });

  // WebSocket connection status
  useEffect(() => {
    setWsConnected(webSocketService.connected);

    // Note: This is a simplified approach. In a production app,
    // you might want to add a specific event listener for connection changes
    const checkConnection = setInterval(() => {
      setWsConnected(webSocketService.connected);
    }, 2000);

    return () => clearInterval(checkConnection);
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">
            Welcome back, {user.username}
          </h1>
          <p className="mt-2 text-gray-400">
            Role: <span className="capitalize text-blue-400">{user.role}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Quick Actions Card */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/labs')}
                className="w-full text-left px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
              >
                Browse Labs
              </button>
              {hasRole(UserRole.LEARNER) && (
                <button 
                  onClick={() => navigate('/runs')}
                  className="w-full text-left px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  My Active Labs
                </button>
              )}
              {hasRole(UserRole.INSTRUCTOR) && (
                <button 
                  onClick={() => navigate('/instructor')}
                  className="w-full text-left px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
                >
                  Instructor Overview
                </button>
              )}
              {hasRole(UserRole.ADMIN) && (
                <button 
                  onClick={() => navigate('/admin')}
                  className="w-full text-left px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                >
                  Admin Panel
                </button>
              )}
            </div>
          </div>

          {/* System Status Card */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">System Status</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">API</span>
                <span className={`flex items-center ${systemHealth?.overall === 'healthy' ? 'text-green-400' : 'text-red-400'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${systemHealth?.overall === 'healthy' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  {systemHealth?.overall === 'healthy' ? 'Online' : 'Issues'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">WebSocket</span>
                <span className={`flex items-center ${wsConnected ? 'text-green-400' : 'text-red-400'}`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${wsConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  {wsConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Labs Available</span>
                <span className="text-blue-400">{labs ? (labs as any[]).length : 'Loading...'}</span>
              </div>
            </div>
          </div>

          {/* Recent Activity Card */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {recentActivity && recentActivity.length > 0 ? (
                recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">{activity.action}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {activity.user?.username || 'System'} • {activity.resource_type}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400">
                  No recent activity to display
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Role-specific sections */}
        {hasRole(UserRole.ADMIN) && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Admin Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Total Users</h3>
                <p className="text-2xl font-bold text-blue-400">
                  {statsLoading ? 'Loading...' : dashboardStats?.users.total_users || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Active Labs</h3>
                <p className="text-2xl font-bold text-green-400">
                  {statsLoading ? 'Loading...' : dashboardStats?.runs.active_runs || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Total Labs</h3>
                <p className="text-2xl font-bold text-purple-400">
                  {statsLoading ? 'Loading...' : dashboardStats?.labs.total_labs || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">System Health</h3>
                <p className={`text-2xl font-bold ${
                  systemHealth?.overall === 'healthy' ? 'text-green-400' : 
                  systemHealth?.overall === 'warning' ? 'text-yellow-400' : 
                  'text-red-400'
                }`}>
                  {systemHealth?.overall === 'healthy' ? 'Good' : 
                   systemHealth?.overall === 'warning' ? 'Warning' : 
                   systemHealth?.overall ? 'Issues' : 'Loading...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {hasRole(UserRole.INSTRUCTOR) && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">Instructor Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Active Student Labs</h3>
                <p className="text-2xl font-bold text-green-400">
                  {dashboardStats?.runs.active_runs || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Students with Labs</h3>
                <p className="text-2xl font-bold text-blue-400">
                  {dashboardStats?.users.active_users || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Recent Activity</h3>
                <p className="text-2xl font-bold text-purple-400">
                  {dashboardStats?.recent.new_runs_24h || 0}
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <button 
                onClick={() => navigate('/instructor')}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
              >
                View Instructor Panel
              </button>
            </div>
          </div>
        )}

        {hasRole(UserRole.LEARNER) && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4">My Learning Progress</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Active Labs</h3>
                <p className="text-2xl font-bold text-green-400">
                  {userRuns?.filter((run: any) => run.state === 'active').length || 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Total Labs Attempted</h3>
                <p className="text-2xl font-bold text-blue-400">
                  {userRuns ? (userRuns as any[]).length : 0}
                </p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white">Completed Labs</h3>
                <p className="text-2xl font-bold text-purple-400">
                  {userRuns?.filter((run: any) => run.state === 'destroyed').length || 0}
                </p>
              </div>
            </div>
            
            <div className="mt-4">
              <button 
                onClick={() => navigate('/runs')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md mr-3"
              >
                View My Labs
              </button>
              <button 
                onClick={() => navigate('/labs')}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
              >
                Browse More Labs
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}